import { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Card, CardActions, Grid, TextField } from '@mui/material';

import { apiUrls } from '../../../constans/apiUrls';
import useApiService from '../../../component/hooks/useApiService';
import { assignBoostToUserListColumns } from '../../../component/common/tableHelper';
import DataGrid from '../../../component/ui/data-grid/DataGrid';
import stringFormat from '../../../component/common/stringFormat';
import Filter from '../../../component/ui/filter/filter';

const AssignBoost = ({ data, close }) => {
  const { executeGet, executePost, executePatch } = useApiService();
  const [dataModel, setDataModel] = useState({});
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 5 });
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [loading, setLoading] = useState(false);

  const setField = (fields) => {
    setDataModel(oldState => ({ ...oldState, ...fields }));
  };

  const columns = useMemo(() => assignBoostToUserListColumns(), []);

  const fetchData = async (filterParams = filter, page = paginationModel.page, pageSize = paginationModel.pageSize) => {
    setLoading(true);
    try {
      const response = await executeGet({
        url: stringFormat(apiUrls.AssignBoostsRuleStatus, data?.tiers?.special?.rule_id),
        params: {
          ...filterParams,
          offset: page * pageSize,
          limit: pageSize,
        },
      });

      setRows(response.items ?? []);
      setTotalCount(response.meta?.total ?? 0);
    } catch (err) {
      console.error('Veri alınamadı:', err);
    } finally {
      setLoading(false);
    }
  };
  const handleReset = () => {
    const emptyFilter = {};
    setFilter(emptyFilter);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    if (paginationModel.page === 0) {
      fetchData({}, 0);
    }
  };

  useEffect(() => {
    setField(data);
    fetchData(filter, paginationModel.page, paginationModel.pageSize);
  }, [paginationModel.page, paginationModel.pageSize]);

  const selectedRow = useMemo(() => {
    if (!rowSelectionModel || rowSelectionModel.length === 0) return null;
    return rows.find((row) => row.user_id === rowSelectionModel[0]) || null;
  }, [rowSelectionModel, rows]);

  const actionList = [
    { name: 'Assign', label: "Ata", order: 2 },
    { name: 'StatusUpdate', label: "Durumu güncelle", order: 2 },
    { name: 'CancelBoost', label: "Atamayı iptal et", order: 2 },
    { name: 'Cancel', variant: 'text', label: "Vazgeç", order: 0 }
  ];

  const filteredActionList = () => {
    if (!selectedRow) return actionList.filter(action => action.name === 'Cancel');;

    const { assigned, status } = selectedRow;

    if (!assigned) {
      return actionList.filter(action => action.name === 'Assign' || action.name === 'Cancel');
    }
    if (assigned && status === "active") {
      return actionList.filter(action => action.name === 'CancelBoost' || action.name === 'Cancel');
    }
    return actionList.filter(action => action.name === 'StatusUpdate' || action.name === 'Cancel');
  };

  const assignBoostToUser = async () => {
    try {
      const res = await executePost({
        url: apiUrls.BoostAssignments,
        data: {
          user_id: selectedRow.user_id,
          rule_id: dataModel?.tiers?.special?.rule_id
        }
      });
      if (res) close && close(res);
    } catch (err) {
      console.error('Assign işleminde hata:', err);
    }
  };

  const updateBoostStatus = async () => {
    const nextStatus = selectedRow.status === "active" ? "cancelled" : "active";

    try {
      const res = await executePatch({
        url: stringFormat(apiUrls.BoostAssignmentsStatusUpdate, selectedRow.assignment_id),
        data: { status: nextStatus }
      });
      if (res) close && close(res);
    } catch (err) {
      console.error('Status update hata aldı:', err);
    }
  };

  const cancelBoostStatus = async () => {
    try {
      const res = await executePost({
        url: stringFormat(apiUrls.BoostAssignmentsSpecialCancel, selectedRow.assignment_id),
      });
      if (res) close && close(res);
    } catch (err) {
      console.error('Status update hata aldı:', err);
    }
  };

  const onActionClick = useCallback((action) => {
    switch (action.name) {
      case 'Assign':
        assignBoostToUser();
        break;
      case 'StatusUpdate':
        updateBoostStatus();
        break;
      case 'CancelBoost':
        cancelBoostStatus();
        break;
      case 'Cancel':
        close && close();
        break;
      default:
        break;
    }
  }, [selectedRow]);

  return (
    <div style={{ padding: '1rem' }}>
      <Filter onFilter={() => fetchData(filter)} onFilterReset={handleReset}>
        <Grid item xs={4}>
          <TextField
            fullWidth
            name="meta_id"
            label="Meta ID"
            variant="outlined"
            value={filter?.meta_id ?? ''}
            onChange={(e) => setFilter({ ...filter, meta_id: e.target.value })}
          />
        </Grid>
      </Filter>

      <Card sx={{ display: 'flex', flexDirection: 'column', height: 'auto' }}>
        <div style={{ height: 400, width: '100%', marginTop: 20 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            rowCount={totalCount}
            paginationMode="server"
            checkboxSelection
            loading={loading}
            getRowId={(row, index) => row.user_id ?? index + 1}
            onRowSelectionModelChange={(newSelection) => setRowSelectionModel(newSelection)}
            rowSelectionModel={rowSelectionModel}
            sx={{
              '& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox': {
                justifyContent: 'center',
                alignItems: 'center',
              },
            }}
          />
        </div>

        <CardActions
          sx={{
            justifyContent: 'flex-end',
            borderTop: '1px solid #e0e0e0',
            p: 2,
            backgroundColor: '#fff',
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
          }}
        >
          {filteredActionList()
            .sort((a, b) => a.order - b.order)
            .map((action) => (
              <Button
                key={action.name}
                variant={action.variant || 'contained'}
                onClick={() => onActionClick(action)}
                sx={{
                  ...(action.variant && {
                    textTransform: 'none',
                    color: '#1976d2',
                    border: 'none',
                    fontWeight: '500',
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }),
                  ...(!action.variant && {
                    textTransform: 'none',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    borderRadius: '10px',
                    fontWeight: 'bold'
                  })
                }}
              >
                {action.label}
              </Button>
            ))}
        </CardActions>
      </Card>
    </div>
  );
};

export default AssignBoost;
